import { useState } from 'react'
import { useRouter } from 'next/router'
import { Button, Flex } from '@viveaesthetics/shared'
import {
  useCustomerIOAnalytics,
  useSegmentAnalytics
} from '@viveaesthetics/shared-domain'
import {
  useFeatureFlags,
  formatDateTimeInTimeZone
} from '@viveaesthetics/elemy-shared-core'

import { AnalyticsEvents } from '@/types/analyticsEvents'
import { ProviderTimeSlot } from '@/hooks/provider/useGetProvidersAvailableWindowByDay'

interface BookingWidgetTimeSlotsProps {
  times: ProviderTimeSlot[]
  providerId: string
  selectedTimeSlotDate?: string
  onTimeSlotDateClick: (startTime: string) => void
  viewProfileLink?: string
  maxSlotsToShowCount?: number
  template?: string[]
}

export const BookingWidgetTimeSlots = ({
  times,
  providerId,
  selectedTimeSlotDate,
  onTimeSlotDateClick,
  viewProfileLink,
  maxSlotsToShowCount = 6,
  template = [`0 1 calc(33.33% - 0.4rem)`]
}: BookingWidgetTimeSlotsProps) => {
  const router = useRouter()
  const customerIOAnalytics = useCustomerIOAnalytics<AnalyticsEvents>()
  const segmentAnalytics = useSegmentAnalytics<AnalyticsEvents>()
  const flags = useFeatureFlags()
  const isSegmentClientDataEnabled = flags['pfp-3891-segment-client-data']
  const [showMoreTimes, setShowMoreTimes] = useState(false)

  const handleClickMore = () => {
    return viewProfileLink
      ? router.push(viewProfileLink)
      : setShowMoreTimes(true)
  }

  const { timeZone } = Intl.DateTimeFormat().resolvedOptions()

  const hasManySlots = times.length > maxSlotsToShowCount
  const hiddenSlotsCount = times.length - maxSlotsToShowCount

  const limitVisibleSlots = hasManySlots && !showMoreTimes
  const modifiedTimes = limitVisibleSlots
    ? times.slice(0, maxSlotsToShowCount - 1)
    : times

  return (
    <Flex
      $space="spacing4"
      template={template}
      wrap
      flex="1 1 100%"
    >
      {modifiedTimes.map(({ startTime }) => (
        <Button
          key={startTime}
          variant="secondary"
          variantSize="small"
          data-test-id="booking-time-button"
          active={selectedTimeSlotDate === startTime}
          padding={['gap8', 'none']}
          onClick={e => {
            e.stopPropagation()
            onTimeSlotDateClick(startTime)
            customerIOAnalytics.trackEvent(
              AnalyticsEvents.BOOKING_TIME_SLOT_SELECTED,
              {
                therapist_id: providerId,
                time_slot: startTime
              }
            )
            segmentAnalytics.trackEvent(
              AnalyticsEvents.BOOKING_TIME_SLOT_SELECTED,
              isSegmentClientDataEnabled
                ? {
                    therapist_id: providerId,
                    time_slot: startTime
                  }
                : undefined
            )
          }}
        >
          {formatDateTimeInTimeZone(startTime, timeZone, 'h:mm aaa')}
        </Button>
      ))}
      {limitVisibleSlots && (
        <Button
          data-test-id="booking-time-more-button"
          onClick={handleClickMore}
          variant="secondary"
          variantSize="small"
        >
          {hiddenSlotsCount} more
        </Button>
      )}
    </Flex>
  )
}
